import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { Trans } from 'react-i18next';

import TooltipIcon from '../../../../../shared/components/TooltipInfoIcon';
import HighlightedPrice from '../../../../components/HighlightedPrice';
import PlanFeaturesList from '../../../../components/PlanFeaturesList';
import { getShortAreaUnit } from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import {
  getCurrencyForAreaUnit,
  getCurrencySignForAreaUnit,
} from '../../../../../shared/helpers/functions/currency';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import rootStore from '../../../rootStore/RootStore';

const FeaturesList = observer(() => {
  const { config } = usePriceConfig(rootStore);
  const { areaUnitStore } = rootStore;
  const { areaUnit } = areaUnitStore;

  const currency = getCurrencyForAreaUnit(areaUnit);
  const currencySign = getCurrencySignForAreaUnit(areaUnit);
  const shortAreaUnit = getShortAreaUnit(areaUnit);

  const monthlyFee =
    config?.MonthlyPayAsYouGo.BaseMonthlyFee.prices[currency].price;
  const datasetImport =
    config?.MonthlyPayAsYouGo.DatasetImport.prices[currency];
  const fieldRegistrationCost =
    config?.MonthlyPayAsYouGo.FieldRegistration.prices[currency].price;
  const fieldStorageCost =
    config?.MonthlyPayAsYouGo.FieldStorage.prices[currency].price;
  const zonesMapCreation =
    config?.MonthlyPayAsYouGo.ZonesVRAMapsCreation.prices[currency];
  const equationMapsCreation =
    config?.MonthlyPayAsYouGo.EquationMapsCreation.prices[currency];
  const yieldCleaningCalibration =
    config?.MonthlyPayAsYouGo.YieldCleaningAndCalibration.prices[currency];
  const yieldSyntheticMapCreationCost =
    config?.MonthlyPayAsYouGo.YieldSyntheticMapCreation.prices[currency].price;
  const itemExportCost =
    config?.MonthlyPayAsYouGo.Export.prices[currency].price;
  const additionalUserSeatCost =
    config?.MonthlyPayAsYouGo.AdditionalUserSeat.prices[currency].price;
  const planetImageryCost =
    config?.MonthlyPayAsYouGo.PlanetImagery.prices[currency].price;
  const samplingPlanCreationCost =
    config?.MonthlyPayAsYouGo.SamplingPlanCreation.prices[currency].price;

  return (
    <PlanFeaturesList
      title={t('plan-select.details.features')}
      items={[
        <Trans
          i18nKey="plans.monthly.details.core"
          values={{ price: monthlyFee, currency: currencySign }}
          components={{
            1: (
              <TooltipIcon tooltip={t('plans.monthly.core-features-tooltip')} />
            ),
            3: <HighlightedPrice renewable />,
          }}
        />,
        <Trans
          i18nKey="plans.monthly.details.field-registration"
          values={{
            price: fieldRegistrationCost,
            currency: currencySign,
            areaUnit: shortAreaUnit,
          }}
          components={{ 1: <HighlightedPrice /> }}
        />,
        <Trans
          i18nKey="plans.monthly.details.field-monitoring-storage"
          values={{
            price: fieldStorageCost,
            currency: currencySign,
            areaUnit: shortAreaUnit,
          }}
          components={{ 1: <HighlightedPrice renewable /> }}
        />,
        <Trans
          i18nKey="plans.monthly.details.dataset-import.description"
          values={{
            areaUnit: shortAreaUnit,
            price: datasetImport?.price,
            currency: currencySign,
          }}
          components={{
            1: (
              <TooltipIcon
                tooltip={t('plans.monthly.details.dataset-import.tooltip')}
              />
            ),
            3: <HighlightedPrice />,
          }}
        />,
        <Trans
          i18nKey="plans.monthly.details.zones-maps-creation.description"
          values={{
            areaUnit: shortAreaUnit,
            price: zonesMapCreation?.price,
            currency: currencySign,
          }}
          components={{
            1: (
              <TooltipIcon
                tooltip={t('plans.monthly.details.zones-maps-creation.tooltip')}
              />
            ),
            3: <HighlightedPrice />,
          }}
        />,
        <Trans
          i18nKey="plans.monthly.details.equation-maps-creation"
          values={{
            areaUnit: shortAreaUnit,
            price: equationMapsCreation?.price,
            currency: currencySign,
          }}
          components={{ 1: <HighlightedPrice /> }}
        />,
        <Trans
          i18nKey="plans.monthly.details.sampling-plan-creation"
          values={{
            areaUnit: shortAreaUnit,
            price: samplingPlanCreationCost,
            currency: currencySign,
          }}
          components={{ 1: <HighlightedPrice /> }}
        />,
        <Trans
          i18nKey="plans.monthly.details.yield-cleaning-calibration"
          values={{
            areaUnit: shortAreaUnit,
            price: yieldCleaningCalibration?.price,
            currency: currencySign,
          }}
          components={{ 1: <HighlightedPrice /> }}
        />,
        <Trans
          i18nKey="plans.monthly.details.yield-synthetic-map-creation"
          values={{
            areaUnit: shortAreaUnit,
            price: yieldSyntheticMapCreationCost,
            currency: currencySign,
          }}
          components={{ 1: <HighlightedPrice /> }}
        />,
        <Trans
          i18nKey="plans.monthly.details.item-export"
          values={{
            areaUnit: shortAreaUnit,
            price: itemExportCost,
            currency: currencySign,
          }}
          components={{ 1: <HighlightedPrice /> }}
        />,
        <Trans
          i18nKey="plans.monthly.details.additional-user-seat"
          values={{
            price: additionalUserSeatCost,
            currency: currencySign,
          }}
          components={{ 1: <HighlightedPrice renewable /> }}
        />,
        <Trans
          i18nKey="plans.details.planet-imagery"
          values={{
            price: planetImageryCost,
            currency: currencySign,
            areaUnit: shortAreaUnit,
          }}
          components={{ 1: <HighlightedPrice /> }}
        />,
      ]}
    />
  );
});

export default FeaturesList;
